import { useState } from "react";
import axios from "axios";
import { Link, useNavigate, useParams, Navigate } from "react-router-dom";
import { API } from "../config";
import { Search, ArrowRight, Target, Sparkles } from "lucide-react";
import { useLanguage } from "../contexts/LanguageContext";
import SEOHead, { ALL_LOCALES } from "../components/SEOHead";
import FAQSection from "../components/marketing/FAQSection";
import SleepAISection from "../components/marketing/SleepAISection";
import SEOCourseSection from "../components/marketing/SEOCourseSection";
import LiveActivityFeed from "../components/marketing/LiveActivityFeed";

const VARIANT_SLUGS = ["ai-visibility", "outrank", "free-scan"];

/**
 * Localized landing page served at /:lang/:slug
 * 3 conversion-angle variants, all 26 languages, each with unique SEO meta.
 */
export default function LocalizedLandingPage({ variant }) {
  const params = useParams();
  const { language, t, setLanguage } = useLanguage();
  const navigate = useNavigate();
  const [url, setUrl] = useState("");
  const [myDomain, setMyDomain] = useState("");
  const [competitor, setCompetitor] = useState("");
  const [busy, setBusy] = useState(false);
  const [scanErr, setScanErr] = useState("");

  // Sync URL locale with app locale (only if valid + different)
  const urlLang = params.lang;
  if (urlLang && ALL_LOCALES.includes(urlLang) && urlLang !== language) {
    setLanguage(urlLang);
  }

  // Guard: if locale in URL isn't supported, redirect to /en/{slug}
  if (urlLang && !ALL_LOCALES.includes(urlLang)) {
    return <Navigate to={`/en/${variant}`} replace />;
  }

  // Pick meta based on variant
  const meta =
    variant === "ai-visibility"
      ? { title: t.lp_aiv_title, desc: t.lp_aiv_desc }
      : variant === "outrank"
      ? { title: t.lp_out_title, desc: t.lp_out_desc }
      : { title: t.lp_scan_title, desc: t.lp_scan_desc };

  const handleScanSubmit = async (e) => {
    e.preventDefault();
    if (!url.trim()) return;
    setBusy(true);
    setScanErr("");
    try {
      const r = await axios.post(`${API}/analyze`, { url: url.trim(), language });
      if (r.data?.analysis_id) {
        navigate(`/results/${r.data.analysis_id}`);
      }
    } catch (err) {
      const d = err?.response?.data?.detail;
      if ([402, 403].includes(err?.response?.status) && typeof d === "string") {
        setScanErr(d);
        return;
      }
      // fall back — just go home with ?url param
      navigate(`/?url=${encodeURIComponent(url.trim())}`);
    } finally {
      setBusy(false);
    }
  };

  const handleStrategySubmit = (e) => {
    e.preventDefault();
    if (!myDomain.trim() || !competitor.trim()) return;
    const qs = new URLSearchParams({ my: myDomain.trim(), vs: competitor.trim() }).toString();
    navigate(`/strategy?${qs}`);
  };

  return (
    <div
      className="min-h-screen themed-section"
      style={{ background: "var(--color-bg)", color: "var(--color-text)" }}
      data-testid={`localized-landing-${variant}`}
    >
      <SEOHead slug={variant} title={meta.title} description={meta.desc} />

      {/* Sticky top bar */}
      <div
        className="sticky top-0 z-40 backdrop-blur border-b"
        style={{
          background: "color-mix(in srgb, var(--color-bg) 80%, transparent)",
          borderColor: "var(--color-border)",
        }}
      >
        <div className="max-w-6xl mx-auto px-6 py-3 flex items-center justify-between">
          <Link to="/" className="font-black text-xl" style={{ color: "var(--color-text)" }}>
            <span style={{ color: "var(--color-primary)" }}>Top</span>RankPulse
          </Link>
          <div className="flex items-center gap-3 text-xs" style={{ color: "var(--color-text-muted)" }}>
            <Link to="/#pricing" className="hover:opacity-80 hidden sm:inline">
              {t.pricing_title || "Pricing"}
            </Link>
            <Link
              to="/login"
              className="px-3 py-1.5 rounded-lg font-bold"
              style={{ background: "var(--color-primary)", color: "var(--color-bg)" }}
              data-testid="lp-nav-login"
            >
              {t.get_started || "Start free"}
            </Link>
          </div>
        </div>
      </div>

      {/* HERO */}
      <section className="relative max-w-6xl mx-auto px-6 lg:px-12 pt-16 pb-20 text-center">
        {variant === "ai-visibility" && (
          <>
            <div
              className="inline-flex items-center gap-2 px-3 py-1 mb-4 rounded-full"
              style={{
                background: "color-mix(in srgb, var(--color-primary) 15%, transparent)",
                color: "var(--color-primary)",
              }}
            >
              <Sparkles className="w-3.5 h-3.5" />
              <span className="text-[10px] font-mono uppercase tracking-widest font-bold">
                GEO · ChatGPT · Perplexity · Gemini
              </span>
            </div>
            <h1
              className="text-4xl sm:text-5xl lg:text-6xl font-black font-['Cabinet_Grotesk'] tracking-tighter leading-[0.95] mb-5"
              style={{ color: "var(--color-text)" }}
            >
              {t.lp_aiv_h1_a}{" "}
              <span
                className="bg-clip-text text-transparent"
                style={{
                  backgroundImage:
                    "linear-gradient(90deg, var(--color-primary) 0%, var(--color-accent) 100%)",
                }}
              >
                {t.lp_aiv_h1_b}
              </span>
            </h1>
            <p className="text-lg max-w-2xl mx-auto mb-10" style={{ color: "var(--color-text-muted)" }}>
              {t.lp_aiv_lead}
            </p>
            <form
              onSubmit={handleScanSubmit}
              className="max-w-xl mx-auto flex gap-2"
              data-testid="lp-scan-form"
            >
              <input
                type="text"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder="https://yourdomain.com"
                className="flex-1 px-4 py-3 rounded-lg border text-sm focus:outline-none"
                style={{
                  background: "var(--color-surface)",
                  borderColor: "var(--color-border)",
                  color: "var(--color-text)",
                }}
                data-testid="lp-scan-input"
              />
              <button
                type="submit"
                disabled={busy}
                className="px-5 py-3 font-bold rounded-lg flex items-center gap-2 hover:scale-105 transition-transform"
                style={{ background: "var(--color-primary)", color: "var(--color-bg)" }}
                data-testid="lp-scan-submit"
              >
                <Search className="w-4 h-4" />
                {t.lp_aiv_cta}
              </button>
            </form>
            {scanErr && <p className="mt-3 text-sm text-red-400" data-testid="landing-scan-error-1">{scanErr}</p>}
          </>
        )}

        {variant === "outrank" && (
          <>
            <div
              className="inline-flex items-center gap-2 px-3 py-1 mb-4 rounded-full"
              style={{
                background: "color-mix(in srgb, var(--color-primary) 15%, transparent)",
                color: "var(--color-primary)",
              }}
            >
              <Target className="w-3.5 h-3.5" />
              <span className="text-[10px] font-mono uppercase tracking-widest font-bold">
                PulseAI Strategy Generator
              </span>
            </div>
            <h1
              className="text-4xl sm:text-5xl lg:text-6xl font-black font-['Cabinet_Grotesk'] tracking-tighter leading-[0.95] mb-5"
              style={{ color: "var(--color-text)" }}
            >
              {t.lp_out_h1_a}{" "}
              <span
                className="bg-clip-text text-transparent"
                style={{
                  backgroundImage:
                    "linear-gradient(90deg, var(--color-primary) 0%, var(--color-accent) 100%)",
                }}
              >
                {t.lp_out_h1_b}
              </span>{" "}
              {t.lp_out_h1_c}
            </h1>
            <p className="text-lg max-w-2xl mx-auto mb-10" style={{ color: "var(--color-text-muted)" }}>
              {t.lp_out_lead}
            </p>
            <form
              onSubmit={handleStrategySubmit}
              className="max-w-2xl mx-auto grid sm:grid-cols-[1fr_1fr_auto] gap-2"
              data-testid="lp-outrank-form"
            >
              <input
                type="text"
                value={myDomain}
                onChange={(e) => setMyDomain(e.target.value)}
                placeholder={t.strat_placeholder_my || "yourdomain.com"}
                className="px-4 py-3 rounded-lg border text-sm focus:outline-none"
                style={{
                  background: "var(--color-surface)",
                  borderColor: "var(--color-border)",
                  color: "var(--color-text)",
                }}
                data-testid="lp-outrank-my"
              />
              <input
                type="text"
                value={competitor}
                onChange={(e) => setCompetitor(e.target.value)}
                placeholder={t.strat_placeholder_competitor || "competitor.com"}
                className="px-4 py-3 rounded-lg border text-sm focus:outline-none"
                style={{
                  background: "var(--color-surface)",
                  borderColor: "var(--color-border)",
                  color: "var(--color-text)",
                }}
                data-testid="lp-outrank-vs"
              />
              <button
                type="submit"
                className="px-5 py-3 font-bold rounded-lg flex items-center justify-center gap-2 hover:scale-105 transition-transform"
                style={{ background: "var(--color-primary)", color: "var(--color-bg)" }}
                data-testid="lp-outrank-submit"
              >
                {t.lp_out_cta}
                <ArrowRight className="w-4 h-4" />
              </button>
            </form>
          </>
        )}

        {variant === "free-scan" && (
          <>
            <div
              className="inline-flex items-center gap-2 px-3 py-1 mb-4 rounded-full"
              style={{
                background: "color-mix(in srgb, var(--color-primary) 15%, transparent)",
                color: "var(--color-primary)",
              }}
            >
              <Sparkles className="w-3.5 h-3.5" />
              <span className="text-[10px] font-mono uppercase tracking-widest font-bold">
                100% FREE · No credit card
              </span>
            </div>
            <h1
              className="text-4xl sm:text-5xl lg:text-6xl font-black font-['Cabinet_Grotesk'] tracking-tighter leading-[0.95] mb-5"
              style={{ color: "var(--color-text)" }}
            >
              {t.lp_scan_h1_a}{" "}
              <span
                className="bg-clip-text text-transparent"
                style={{
                  backgroundImage:
                    "linear-gradient(90deg, var(--color-primary) 0%, var(--color-accent) 100%)",
                }}
              >
                {t.lp_scan_h1_b}
              </span>
            </h1>
            <p className="text-lg max-w-2xl mx-auto mb-10" style={{ color: "var(--color-text-muted)" }}>
              {t.lp_scan_lead}
            </p>
            <form
              onSubmit={handleScanSubmit}
              className="max-w-xl mx-auto flex gap-2"
              data-testid="lp-scan-form"
            >
              <input
                type="text"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder="https://yourdomain.com"
                className="flex-1 px-4 py-3 rounded-lg border text-sm focus:outline-none"
                style={{
                  background: "var(--color-surface)",
                  borderColor: "var(--color-border)",
                  color: "var(--color-text)",
                }}
                data-testid="lp-scan-input"
              />
              <button
                type="submit"
                disabled={busy}
                className="px-5 py-3 font-bold rounded-lg flex items-center gap-2 hover:scale-105 transition-transform"
                style={{ background: "var(--color-primary)", color: "var(--color-bg)" }}
                data-testid="lp-scan-submit"
              >
                <Search className="w-4 h-4" />
                {t.lp_scan_cta}
              </button>
            </form>
            {scanErr && <p className="mt-3 text-sm text-red-400" data-testid="landing-scan-error-3">{scanErr}</p>}
          </>
        )}
      </section>

      {/* Reuse existing, already-translated marketing sections */}
      <SleepAISection />
      <SEOCourseSection />
      <FAQSection />

      {/* Footer mini */}
      <footer
        className="py-10 text-center text-xs border-t"
        style={{
          color: "var(--color-text-muted)",
          borderColor: "var(--color-border)",
          background: "var(--color-surface-alt)",
        }}
      >
        <div className="max-w-5xl mx-auto px-6 flex flex-wrap items-center justify-center gap-4">
          <Link to="/" className="hover:opacity-80">TopRankPulse</Link>
          <span>·</span>
          <Link to="/privacy" className="hover:opacity-80">Privacy</Link>
          <span>·</span>
          <Link to="/terms" className="hover:opacity-80">Terms</Link>
          <span>·</span>
          <Link to={`/en/${variant}`} className="hover:opacity-80">EN</Link>
          <span>·</span>
          <Link to={`/sk/${variant}`} className="hover:opacity-80">SK</Link>
          <span>·</span>
          <Link to={`/de/${variant}`} className="hover:opacity-80">DE</Link>
          <span>·</span>
          <Link to={`/fr/${variant}`} className="hover:opacity-80">FR</Link>
        </div>
      </footer>

      <LiveActivityFeed />
    </div>
  );
}

export { VARIANT_SLUGS };
